/* eslint-disable @typescript-eslint/no-explicit-any */
import '@tock/tokens/dist/omakase.scss';
import { AppContextProvider } from '@tock/money';
import {
  ApplicationContext,
  GoogleMapsContext,
  MobileNativeContext,
} from 'providers/withApplicationContext';
import { BaseHead } from './BaseHead';
import { ConsentRestrictedScripts } from 'components/consent-restricted-scripts/ConsentRestrictedScripts';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider as EmotionThemeProvider, Theme, css } from '@emotion/react';
import { FeatureFlagContext } from '@tock/shared/utils/featureFlagContext';
import { IIdleTimer, withIdleTimer } from 'react-idle-timer';
import { IntlProvider } from 'react-intl';
import { ManageCookieConsentState } from '@tock/shared/components/cookie-consent/ManageCookieConsentState';
import { PageTypeData } from 'actions/ConsumerPageActions';
import { PatronLocationContext } from './components/patron-location/PatronLocationContext';
import { PreviewModeSecondaryHeader } from 'components/header/PreviewModeSecondaryHeader';
import { Route, RouteComponentProps, Switch, matchPath, withRouter } from 'react-router-dom';
import { StylesProvider, ThemeProvider, createGenerateClassName } from '@material-ui/core/styles';
import { TockCookieName } from '@tock/shared/constants/browser-storage/TockCookies';
import { TockProductType } from '@tock/shared/types/productFeatures';
import { clearCsrfInterval, setupCsrfTokenRefresh } from '@tock/shared/utils/csrfTokenUtils';
import { core, safeEnumCompare } from '@tock/coretypes';
import { currentMetroAreaSelector } from 'selectors/metroAreaSelectors';
import { defaultMemoize } from 'reselect';
import { getApp } from 'selectors/appSelector';
import { getCookie } from '@tock/shared/utils/cookieUtils';
import { getDomain, inAvailabilitySearch } from 'utils/domainUtils';
import { getTheme } from 'theme/Theme';
import { jss } from 'utils/jssInsertionOrderUtils';
import { loadable } from 'utils/loadable';
import { parse as parseQueryString } from '@tock/query-string';
import { redirectIfNecessary } from 'utils/redirect';
import { scrollMemory } from '@tock/shared/utils/scroll';
import AndroidDecomAlert from 'components/AndroidDecomAlert';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ConsumerProductFeatureProvider from 'components/product/ConsumerProductFeatureProvider';
import ErrorBoundary, { GraphqlErrorBoundary } from 'utils/ErrorBoundary';
import GlobalStyles from 'globalStyles';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import OAuthRedirRoute from 'routes/unprotected/OAuthRedirRoute';
import React from 'react';
import ReactModal from 'react-modal';
import ReduxStateMonitors from 'components/ReduxStateMonitors';
import Toast from 'components/Toast';
import TransitionLoading from 'components/TransitionLoading';
import _ from 'lodash';
import clsx from 'clsx';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import consumerProtos from 'utils/consumerProto';

const LogoutRoute = loadable(
  () => /* webpackPrefetch: true */ import('routes/protected/LogoutRoute')
);
const GiftCardRoute = loadable(
  () => /* webpackPrefetch: true */ import('routes/gift/GiftCardRoute')
);
const BusinessRoute = loadable(
  () => /* webpackPrefetch: true */ import('routes/business/BusinessRoute')
);
const StandardHeaderRoute = loadable(
  () => /* webpackPrefetch: true */ import('routes/StandardHeaderRoute')
);
const ExperimentalRoute = loadable(
  () => /* webpackPrefetch: true */ import('experimental/ExperimentalRoute')
);

const IdleTimerComponent: React.FC<IIdleTimer> = ({ children }) => {
  return <>{children}</>;
};
const IdleTimer = withIdleTimer(IdleTimerComponent);

const { DomainConfig } = consumerProtos;

interface OwnProps {
  /**
   * used in SSR only, we do 2 render passes, one where styles are generated for css-in-js, and one without
   */
  disableGeneration: boolean;
}

interface ConnectProps extends DefaultConnectProps {
  appContext: StoreType['app']['appContext'];
  consumerPage: StoreType['consumerPage'];
  currentMetroArea?: core.MetroArea;
  experiments: StoreType['experiments'];
  locationHistory: StoreType['history']['locationHistory'];
  theme: StoreType['theme'];
  timezone: string;
  ui: StoreType['ui'];
}

interface MatchParams {
  businessDomain?: string;
  widget?: string;
}

type Props = OwnProps & ConnectProps & RouteComponentProps<MatchParams>;

const generateClassName = createGenerateClassName();

class Application extends BaseComponent<Props> {
  scrollUnlisten?: () => void;
  idleTimer?: IIdleTimer;
  csrfInterval: NodeJS.Timeout | null;

  constructor(props: Props) {
    super(props);
    this.csrfInterval = null;
  }

  componentDidMount() {
    const { history, currentMetroArea, actions } = this.props;
    this.scrollUnlisten = scrollMemory(history, [
      { path: '/widget-builder/' },
      { path: '/profile', exact: true },
      { path: '/profile/reservations', exact: true },
      { path: '/profile/saved', exact: false },
    ]);
    // We call fetchData to create a session
    if (!currentMetroArea) {
      actions.app.detectLocation().catch().finally(this.fetchData);
    } else {
      this.fetchData();
    }

    this.redirectV2IfNecessary();
    ReactModal.setAppElement('#Root');

    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles?.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const {
      actions,
      app,
      location: { pathname, search },
    } = nextProps;

    if (
      _.includes(pathname, 'signin') ||
      _.includes(pathname, 'login') ||
      _.includes(pathname, 'logout') ||
      _.includes(pathname, 'widget-builder') ||
      _.includes(pathname, 'oauth')
    ) {
      return;
    }

    const oldPathname = this.props.location.pathname;
    const oldDomain = getDomain(oldPathname);
    const newDomain = getDomain(pathname);
    if (oldDomain !== newDomain) {
      actions.app.fetchConfig(newDomain);
    }

    const searchResultsMatch = this.getIsSearchResults(pathname, search);

    if (
      oldPathname !== pathname &&
      newDomain !== 'profile' &&
      newDomain !== 'concierge' &&
      !searchResultsMatch &&
      newDomain !== app?.config?.business?.domainName
    ) {
      this.fetchConsumerPage(pathname);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { currentMetroArea: prevCurrentMetroArea } = prevProps;
    const {
      currentMetroArea,
      location: { pathname, search },
    } = this.props;
    const searchResultsMatch = this.getIsSearchResults(pathname, search);
    if (prevCurrentMetroArea?.name !== currentMetroArea?.name && !searchResultsMatch) {
      this.fetchConsumerPage(pathname);
    }
    this.redirectV2IfNecessary();
    if (getCookie(TockCookieName.SYNDICATE)) {
      this.handleTheming();
    }
  }

  componentWillUnmount() {
    if (this.scrollUnlisten) {
      this.scrollUnlisten();
    }
    (this.props.history as any).unlisten();

    clearCsrfInterval({ current: this.csrfInterval });
  }

  getIsSearchResults(pathname: string, search: string): boolean {
    const searchMatch = matchPath(pathname, {
      path: '/:type(city|group)/:value/search',
    });
    const togoSearchMatch = matchPath(pathname, {
      path: '/:type(togo)/search',
    });
    const isSearchResults = !!searchMatch || !!togoSearchMatch;

    return isSearchResults;
  }

  fetchConsumerPage(pathname: string) {
    const {
      actions,
      app: { settings },
      currentMetroArea,
    } = this.props;
    const type: PageTypeData = { type: 'EXPLORETOCK' };
    actions.consumerPage
      .searchPage(pathname, type, settings.PREVIEW_MODE === 'preview', currentMetroArea?.id)
      .catch(_.noop);
  }

  handleTheming() {
    const { backgroundBase, brandMuted, brandPrimary, font } = this.props.theme;
    if (!_.isEmpty(backgroundBase) && !_.isEmpty(brandMuted) && !_.isEmpty(brandPrimary)) {
      this.setColors(backgroundBase, brandMuted, brandPrimary);
    }
    if (font === 'system') {
      this.setFonts('system');
    }
  }

  setColors(backgroundBase: string, brandMuted: string, brandPrimary: string) {
    const styleTag = document.body.style;
    styleTag.setProperty('--background_color-brand', `#${brandPrimary}`);
    styleTag.setProperty('--background_color-brand_disabled', `#${brandMuted}`);
    styleTag.setProperty('--border_color-active', `#${brandPrimary}`);
    styleTag.setProperty('--text_color-primary', `#${brandPrimary}`);
    styleTag.setProperty('--icon_color-primary', `#${brandPrimary}`);
    styleTag.setProperty('--background_color-light', `#${backgroundBase}`);
  }

  setFonts(font: string) {
    const styleTag = document.body.style;
    styleTag.setProperty('--sans-regular-font', `"${font}-regular"`);
    styleTag.setProperty('--sans-medium-font', `"${font}-medium"`);
    styleTag.setProperty('--sans-bold-font', `"${font}-bold"`);
  }

  async fetchData() {
    const { location, app, features, globalFeatures } = this.props;
    const query = parseQueryString(location.search);
    const keyValues: Dictionary<string> = {};
    _.forEach(query, (value, fullKey: string) => {
      const key = _.toLower(fullKey);
      if (_.startsWith(key, 'utm_')) {
        keyValues[key] = value;
      }
    });

    const hasEnableCsrfToken = !!globalFeatures.ENABLE_CSRF_TOKEN || !!features.ENABLE_CSRF_TOKEN;

    if (!app.patron) {
      // Don't change this without understanding various ways of boostrapping calendar on
      // server side and client side.
      if (app.jwtToken) {
        requestIdleCallback(() => this.loginWithTockJwt(hasEnableCsrfToken), {
          timeout: 100,
        });
      }

      const context = this.props.app?.config?.domain?.context;
      if (safeEnumCompare(context, DomainConfig.Context, DomainConfig.Context.WWW)) {
        const pathname = location.pathname;
        const searchResultsMatch = this.getIsSearchResults(pathname, location.search);
        if (!searchResultsMatch) {
          this.fetchConsumerPage(pathname);
        }
      }
    }
  }

  // Don't change this without understanding various ways of boostrapping calendar on
  // server side and client side.
  async loginWithTockJwt(hasEnableCsrfToken: boolean) {
    const { app, actions } = this.props;
    if (app.jwtToken) {
      await setupCsrfTokenRefresh(actions.csrfTokenActions.getCsrfToken, hasEnableCsrfToken, true, {
        current: this.csrfInterval,
      });
      actions.tockGiftCard.checkTockGiftCardBalance();
      const profileResponsePromise = actions.app.fetchPatronProfile();
      actions.app.fetchRoles();
      return profileResponsePromise;
    }

    return Promise.resolve(undefined);
  }

  redirectV2IfNecessary() {
    const { app, history, location } = this.props;
    const url = location.pathname + location.search;
    const context = app?.config?.domain?.context;
    return redirectIfNecessary(url, context, history.replace);
  }

  /**
   * currently these come back as empty strings if not set. in the future we shoudl initialize this to
   * be undefined in the state
   */
  getTheme = defaultMemoize((backgroundColor: string, primaryColor: string, font: string) =>
    getTheme('light', {
      backgroundColor: _.size(backgroundColor) !== 0 ? `#${backgroundColor}` : undefined,
      primaryColor: _.size(primaryColor) !== 0 ? `#${primaryColor}` : undefined,
      font: _.size(font) !== 0 ? font : undefined,
    })
  );

  render() {
    const {
      app: { settings },
    } = this.props;

    const {
      app,
      consumerPage,
      currency,
      disableGeneration,
      features,
      history,
      location,
      locale,
      timezone,
      theme,
      actions,
    } = this.props;

    const { backgroundBase, brandPrimary, font } = theme;

    if (!app.isInitialized) {
      return null;
    }

    const page = _.first(_.filter(_.split(location.pathname, '/'))) || 'home';
    const searchPage = inAvailabilitySearch(location.pathname);
    const configIsLoading =
      (app.fastRoutingBetweenContext && app.configRequestInProgress && !searchPage) ||
      !app.isInitialized ||
      !consumerPage.isInitialized;

    const generatedTheme = this.getTheme(backgroundBase, brandPrimary, font);

    const widgetPath = '/:businessDomain/:widget(widget)';
    const isWidget = !_.isNil(
      matchPath(location.pathname, {
        path: widgetPath,
      })
    );

    if (!BROWSER && generatedTheme && generatedTheme.props) {
      generatedTheme.props.MuiUseMediaQuery = (this.props.staticContext as any).MuiUseMediaQuery;
    }

    return (
      <StylesProvider
        disableGeneration={disableGeneration}
        generateClassName={generateClassName}
        jss={jss}
      >
        <ThemeProvider theme={generatedTheme}>
          <EmotionThemeProvider theme={generatedTheme}>
            <>
              <GlobalStyles />
              <CssBaseline />
              <BaseHead />
              <AppContextProvider
                appContext={app.appContext}
                currency={currency}
                locale={locale}
                features={features}
                history={history}
                timeZone={timezone}
              >
                <ApplicationContext.Provider value={app}>
                  <GoogleMapsContext.Provider
                    value={{ loaded: app.googleMapsLoaded, error: app.googleMapsLoadError }}
                  >
                    <MobileNativeContext.Provider
                      value={{
                        mobileNative: app.inReactNative,
                        reactNative: app.inReactNative,
                      }}
                    >
                      <PatronLocationContext>
                        <IntlProvider locale={locale}>
                          <FeatureFlagContext.Provider value={features}>
                            <ManageCookieConsentState
                              onUpdateCookieConsentLevel={
                                actions.cookieConsent.updateCookieConsentLevel
                              }
                              ignoreConsentSettings={false}
                            />
                            <ConsentRestrictedScripts />
                            <ReduxStateMonitors />
                            <ConsumerProductFeatureProvider
                              productType={app.config?.business?.tockProductType as TockProductType}
                            >
                              <div className={clsx('Wrapper', { 'is-widget': isWidget })}>
                                {BROWSER && (
                                  <IdleTimer
                                    ref={(ref: IIdleTimer) => {
                                      this.idleTimer = ref;
                                    }}
                                    element={document}
                                    throttle={3000}
                                    timeout={1000 * 340 /* */}
                                  />
                                )}
                                <Toast />
                                <TransitionLoading />
                                <LoadingComponent
                                  isLoading={configIsLoading}
                                  isBallPulse={true}
                                  data-testid="loading-loading"
                                >
                                  <AndroidDecomAlert />
                                  <GraphqlErrorBoundary>
                                    <ErrorBoundary>
                                      <div
                                        css={(theme: Theme) => css`
                                          ${theme.breakpoints.up('sm')} {
                                            background-color: ${page === 'playlist' ||
                                            location.pathname.includes('/profile/account/playlists')
                                              ? theme.colors.bgLight
                                              : undefined};
                                          }
                                        `}
                                        className={clsx('PageWrapper', `is-${page}`)}
                                      >
                                        {settings.PREVIEW_MODE === 'preview' && (
                                          <PreviewModeSecondaryHeader />
                                        )}
                                        <Switch location={location}>
                                          <Route
                                            path={`/oauth-redir`}
                                            component={OAuthRedirRoute}
                                          />
                                          {/* this is here to avoid the standard header being included */}
                                          <Route
                                            path={[
                                              '/:businessDomain/continue-purchase',
                                              '/:businessDomain/complete-transfer',
                                              '/:businessDomain/checkout',
                                              '/:businessDomain/package',
                                              '/:businessDomain/syndicate',
                                              widgetPath,
                                            ]}
                                            component={BusinessRoute}
                                          />
                                          <Route
                                            path="/gift/card/checkout"
                                            component={GiftCardRoute}
                                          />
                                          <Route
                                            path="/gift/card/redeem"
                                            component={GiftCardRoute}
                                          />
                                          <Route path="/logout" component={LogoutRoute} />
                                          {features.EXPERIMENTAL_DIRECTORY && (
                                            <Route
                                              path="/experimental"
                                              component={ExperimentalRoute}
                                            />
                                          )}
                                          <Route component={StandardHeaderRoute} />
                                        </Switch>
                                      </div>
                                    </ErrorBoundary>
                                  </GraphqlErrorBoundary>
                                </LoadingComponent>
                              </div>
                            </ConsumerProductFeatureProvider>
                          </FeatureFlagContext.Provider>
                        </IntlProvider>
                      </PatronLocationContext>
                    </MobileNativeContext.Provider>
                  </GoogleMapsContext.Provider>
                </ApplicationContext.Provider>
              </AppContextProvider>
            </>
          </EmotionThemeProvider>
        </ThemeProvider>
      </StylesProvider>
    );
  }
}

export default withRouter(
  connect<OwnProps & RouteComponentProps<MatchParams>, ConnectProps>(
    Application,
    (state: StoreType) => {
      return {
        app: getApp(state),
        consumerPage: state.consumerPage,
        currentMetroArea: currentMetroAreaSelector(state),
        theme: state.theme,
        ui: state.ui,
        // Don't remove this duplicate connection.
        // Required by ContextProvider.
        appContext: state.app.appContext,
        currency: state.app.currency,
        experiments: state.experiments,
        features: state.features.features,
        locale: state.app.locale,
        locationHistory: state.history.locationHistory,
        timezone: state.app.timeZone,
      };
    }
  )
);
